/**
 * Quote and contract PDFs in the browser (ADR-017): the shared Typst template
 * (`crates/teslyn-core/templates/commercial-document.typ`, the same file teslyn-core renders in Rust) compiled by
 * the typst.ts WASM build. The data bundle comes from `@teslyn/domain` (`buildQuoteDocument`,
 * `buildContractDocument`); this module only adds the brand colour tokens and turns the bundle into PDF bytes.
 *
 * The compiler (about 10.8 MB gzipped) and the fonts load on the first render only, from this portal's own address:
 * no CDN, no server function, and the data never leaves the browser.
 */
import type { DocumentBundle } from '@teslyn/domain';
import template from '../../../../crates/teslyn-core/templates/commercial-document.typ?raw';
import tokensJson from '../../../../brand/tokens/tokens.json';

interface TokenFile {
  readonly color: {
    readonly tokens: ReadonlyArray<{ readonly name: string; readonly value: { readonly light: string } }>;
  };
}

/** Light-theme colour tokens by name, as teslyn-core passes them to its templates. */
export function lightTokens(file: TokenFile = tokensJson as TokenFile): Record<string, string> {
  return Object.fromEntries(file.color.tokens.map((t) => [t.name, t.value.light]));
}

/** The `sys.inputs.data` JSON of the template. */
export function templateInput(doc: DocumentBundle, brand = ''): string {
  return JSON.stringify({ doc, tokens: lightTokens(), brand });
}

export const TEMPLATE_SOURCE: string = template;

/** Where the compiler module and the fonts come from (browser: fetched from this portal; tests: read from disk). */
export interface RendererAssets {
  readonly wasm: () => Promise<Uint8Array | ArrayBuffer>;
  readonly fonts: () => Promise<readonly Uint8Array[]>;
}

export class DocumentRenderError extends Error {
  override readonly name = 'DocumentRenderError';
}

interface Compiler {
  addSource(path: string, source: string): void;
  compile(o: {
    mainFilePath: string;
    format: number;
    inputs: Record<string, string>;
    diagnostics: 'unix';
  }): Promise<{ result?: Uint8Array; diagnostics?: unknown }>;
}

let compilerPromise: Promise<Compiler> | null = null;
let compilerAssets: RendererAssets | null = null;

async function compiler(assets: RendererAssets): Promise<Compiler> {
  if (!compilerPromise || compilerAssets !== assets) {
    compilerAssets = assets;
    compilerPromise = (async () => {
      const [{ createTypstCompiler }, { loadFonts, disableDefaultFontAssets }] = await Promise.all([
        import('@myriaddreamin/typst.ts/compiler'),
        import('@myriaddreamin/typst.ts/options.init'),
      ]);
      const [wasm, fonts] = await Promise.all([assets.wasm(), assets.fonts()]);
      const c = createTypstCompiler();
      await c.init({
        getModule: () => wasm,
        beforeBuild: [disableDefaultFontAssets(), loadFonts([...fonts])],
      });
      return c as unknown as Compiler;
    })();
    compilerPromise.catch(() => {
      compilerPromise = null;
    });
  }
  return compilerPromise;
}

/** PDF bytes of a quote or contract bundle. */
export async function renderPdf(
  doc: DocumentBundle,
  assets: RendererAssets,
  brand = '',
): Promise<Uint8Array> {
  const c = await compiler(assets);
  c.addSource('/commercial-document.typ', TEMPLATE_SOURCE);
  const out = await c.compile({
    mainFilePath: '/commercial-document.typ',
    format: 1, // CompileFormatEnum.pdf
    inputs: { data: templateInput(doc, brand) },
    diagnostics: 'unix',
  });
  if (!out.result) {
    throw new DocumentRenderError(
      `The document could not be laid out: ${String(out.diagnostics ?? 'unknown error')}`,
    );
  }
  return out.result;
}

/** Gzip-compressed bytes (the portal ships the compiler as .wasm.gz) are inflated; plain bytes pass through. */
export async function inflateIfGzip(bytes: ArrayBuffer): Promise<ArrayBuffer> {
  const head = new Uint8Array(bytes, 0, 2);
  if (head[0] !== 0x1f || head[1] !== 0x8b) return bytes;
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
}
